/**
 * @file DecorLayerList.tsx
 * @description Группа «Рисунки» секции (phase19.16 Р2; uiux-design-r2 §2,
 * §10; решение 2026-10-09 «рисунки у секции делаем»): до 3 карточек
 * (`DecorCard`), «+ Добавить рисунок» — медиатека с PNG/SVG/WebP; на
 * пределе кнопка скрыта. SB120 этап 1 (uiux-sb120-stage1 §2.7): место —
 * точкой на схеме, размер — числом для ПК и телефона; новый рисунок — по
 * центру, M 160 px (`defaultSectionDecor` из shared).
 */

import { defaultSectionDecor, SECTION_LOOK_LIMITS, SITE_LIMITS, type SectionDecor } from "@chaiberi/shared";
import { Plus } from "lucide-react";
import { useState } from "react";

import { Button } from "../../../ui/button";
import { MediaPickerDialog } from "../../../site-view/media/MediaPickerDialog";
import { useSectionForm } from "../section-form-context";
import { DecorCard } from "./DecorCard";

/** Рисунки — с прозрачным фоном. */
const DECOR_ACCEPT = ["image/png", "image/svg+xml", "image/webp"] as const;

interface Props {
  decor: SectionDecor[];
  columns: number;
  onChange: (decor: SectionDecor[]) => void;
}

export function DecorLayerList({ decor, columns, onChange }: Props) {
  const { highlight, theme } = useSectionForm();
  const [picking, setPicking] = useState(false);
  const phoneFactor = theme?.spacing.phoneFactor ?? SECTION_LOOK_LIMITS.phoneFactor.default;

  return (
    <div className="flex flex-col gap-3" onMouseEnter={() => highlight("decor")} onMouseLeave={() => highlight(null)}>
      {decor.map((d, index) => (
        <DecorCard
          key={`${d.mediaId}-${index}`}
          decor={d}
          index={index}
          columns={columns}
          phoneFactor={phoneFactor}
          onChange={(next) => onChange(decor.map((x, i) => (i === index ? next : x)))}
          onRemove={() => onChange(decor.filter((_, i) => i !== index))}
        />
      ))}
      {decor.length < SITE_LIMITS.sectionDecor ? (
        <Button variant="outline" className="self-start" onClick={() => setPicking(true)}>
          <Plus aria-hidden />
          Добавить рисунок
        </Button>
      ) : (
        <p className="type-caption text-on-surface-variant">
          {SITE_LIMITS.sectionDecor} из {SITE_LIMITS.sectionDecor} — больше рисунков у секции не бывает.
        </p>
      )}
      <MediaPickerDialog
        open={picking}
        title="Рисунок секции"
        hint="PNG, SVG или WebP с прозрачным фоном: веточка, цветок, пятно краски."
        accept={DECOR_ACCEPT}
        onClose={() => setPicking(false)}
        onSelect={(media) => {
          setPicking(false);
          onChange([...decor, defaultSectionDecor(media.id)]);
        }}
      />
    </div>
  );
}
